import type { ComputerUseReadiness } from './types';

// Must match Rust `RPA_COMPUTER_USE_NOT_READY` in executor/mod.rs
export const RPA_COMPUTER_USE_NOT_READY_ERROR_CODE = 'RPA_COMPUTER_USE_NOT_READY';

type PermissionState = ComputerUseReadiness['screen_capture'];
type ExecutorClass = ComputerUseReadiness['ready_for'][number];

const PERMISSION_STATES: ReadonlySet<string> = new Set<PermissionState>([
  'granted',
  'denied',
  'granted_needs_relaunch',
  'unsupported',
]);
const EXECUTOR_CLASSES: ReadonlySet<string> = new Set<ExecutorClass>([
  'computer_use',
  'file_tools',
]);

const isPermissionState = (value: unknown): value is PermissionState =>
  typeof value === 'string' && PERMISSION_STATES.has(value);

const isExecutorClass = (value: unknown): value is ExecutorClass =>
  typeof value === 'string' && EXECUTOR_CLASSES.has(value);

/** IPC `details` are untyped */
export const parseComputerUseReadiness = (
  value: unknown
): ComputerUseReadiness | null => {
  if (typeof value !== 'object' || value === null) return null;
  const { screen_capture, accessibility, ready_for } = value as Record<string, unknown>;
  if (!isPermissionState(screen_capture) || !isPermissionState(accessibility))
    return null;
  if (!Array.isArray(ready_for) || !ready_for.every(isExecutorClass)) return null;
  return { screen_capture, accessibility, ready_for };
};

export const readComputerUseRefusal = (error: unknown): ComputerUseReadiness | null => {
  if (!(error instanceof Error) || !('errorCode' in error) || !('details' in error))
    return null;
  if (error.errorCode !== RPA_COMPUTER_USE_NOT_READY_ERROR_CODE) return null;
  return parseComputerUseReadiness(error.details);
};

/** Rust marks both states `unsupported` together on the Mac App Store build */
export const isComputerUseUnsupported = (readiness: ComputerUseReadiness): boolean =>
  readiness.screen_capture === 'unsupported';
